import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_COLORS = {
  Mathematics: '#448AFF', Maths: '#448AFF',
  Science: '#66BB6A', 'Environmental Studies': '#66BB6A', EVS: '#66BB6A',
  Physics: '#66BB6A', Chemistry: '#66BB6A', Biology: '#66BB6A',
  'Physical Science': '#66BB6A',
  English: '#AB47BC',
  Hindi: '#FF6B6B', Telugu: '#FF6B6B', 'Telugu (Second Language)': '#FF6B6B',
  History: '#E6A800', 'Social Studies': '#E6A800', 'Social Science': '#E6A800',
  Civics: '#E6A800', Geography: '#E6A800', 'History and Civics': '#E6A800',
  'Artificial Intelligence': '#448AFF',
};

function getAccentColor(subject) {
  return SUBJECT_COLORS[subject] || '#448AFF';
}

function getSubjectFromEntry(entry) {
  if (entry.topic_ref?.includes('/')) return entry.topic_ref.split('/')[0];
  return 'General';
}

function getTopicFromEntry(entry) {
  if (entry.topic_ref?.includes('/')) return entry.topic_ref.split('/').slice(1).join('/');
  return entry.topic_ref || '';
}

function formatDiaryDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

const MILESTONES = [
  { count: 5, name: 'Dream Starter', icon: '🌱' },
  { count: 10, name: 'Dream Explorer', icon: '🔭' },
  { count: 25, name: 'Dream Adventurer', icon: '🗺️' },
  { count: 50, name: 'Dream Master', icon: '🏆' },
  { count: 100, name: 'Dream Legend', icon: '✨' },
];

function getNextMilestone(total) {
  for (const m of MILESTONES) {
    if (total < m.count) return m;
  }
  return null;
}

function getCurrentMilestone(total) {
  let current = null;
  for (const m of MILESTONES) {
    if (total >= m.count) current = m;
  }
  return current;
}

export default function DreamDiary({ user }) {
  const [entries, setEntries] = useState([]);
  const [totalDreams, setTotalDreams] = useState(0);
  const [totalStars, setTotalStars] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  const petName = localStorage.getItem('you_pet_name') || 'Your pet';
  const email = localStorage.getItem('you_user_email') || '';

  useEffect(() => {
    if (!email) { setLoading(false); return; }

    const timeout = setTimeout(() => setLoading(false), 5000);

    fetch(`${API}/dream-diary?user_email=${encodeURIComponent(email)}&days=365`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        setEntries(data.entries || []);
        setTotalDreams(data.total_dreams_explored || 0);
        setTotalStars(data.total_stars || 0);
      })
      .catch(() => {
        setEntries([]);
        setTotalDreams(0);
        setTotalStars(0);
      })
      .finally(() => { clearTimeout(timeout); setLoading(false); });
  }, [email]);

  if (!email) return (
    <div style={{
      textAlign: 'center', padding: '20px 16px',
      background: '#FFFFFF', border: '1.5px dashed #CCCCCC',
      borderRadius: 4, marginBottom: 16,
    }}>
      <p style={{ fontSize: 28 }}>📖</p>
      <p style={{
        fontSize: 18, fontFamily: "'Patrick Hand', cursive",
        color: '#5E35B1', margin: '8px 0',
      }}>
        Start your Dream Diary!
      </p>
      <p style={{
        fontSize: 15, fontFamily: "'Inter', sans-serif",
        color: '#666666', margin: '0 0 12px',
      }}>
        Sign in to save every dream you explore — build your personal encyclopedia!
      </p>
    </div>
  );

  if (loading) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{
          height: 36, borderRadius: 4, marginBottom: 12,
          background: 'rgba(0,0,0,0.04)',
          animation: 'petIdle 2.5s ease-in-out infinite',
        }} />
        {[1, 2].map(i => (
          <div key={i} style={{
            height: 80, borderRadius: 4, marginBottom: 8,
            background: 'rgba(0,0,0,0.04)',
            animation: 'petIdle 2.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  // Extract unique subjects
  const subjects = [...new Set(entries.map(getSubjectFromEntry))].filter(s => s !== 'General').sort();
  const subjectCount = subjects.length;

  // Filter entries
  const filtered = filter === 'All' ? entries : entries.filter(e => getSubjectFromEntry(e) === filter);

  // Milestone progress
  const nextMilestone = getNextMilestone(totalDreams);
  const currentMilestone = getCurrentMilestone(totalDreams);

  // Empty state
  if (entries.length === 0) {
    return (
      <div style={{ marginBottom: 20 }}>
        <h3 style={{
          fontSize: 22, fontFamily: "'Patrick Hand', cursive", fontWeight: 400,
          color: '#5E35B1', margin: '0 0 12px',
        }}>
          {petName}'s Dream Diary
        </h3>
        <div style={{
          textAlign: 'center', padding: '24px 16px',
          border: '1.5px dashed #CCCCCC', borderRadius: 4,
          background: '#FFFFFF',
        }}>
          <span style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>📖</span>
          <p style={{
            fontSize: 16, fontFamily: "'Patrick Hand', cursive",
            color: '#1A1A2E', margin: '0 0 4px',
          }}>
            {petName}'s dream diary is empty...
          </p>
          <p style={{
            fontSize: 14, fontFamily: "'Inter', sans-serif", fontWeight: 400,
            color: '#999999', margin: 0,
          }}>
            Explore your first dream on the Home tab!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Header */}
      <h3 style={{
        fontSize: 22, fontFamily: "'Patrick Hand', cursive", fontWeight: 400,
        color: '#5E35B1', margin: '0 0 12px',
      }}>
        {petName}'s Dream Diary
      </h3>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'Dreams', value: totalDreams, color: '#448AFF', bg: 'rgba(68,138,255,0.08)', border: 'rgba(68,138,255,0.15)' },
          { label: 'Stars', value: totalStars, color: '#E6A800', bg: 'rgba(255,215,64,0.08)', border: 'rgba(255,215,64,0.15)' },
          { label: 'Subjects', value: subjectCount, color: '#66BB6A', bg: 'rgba(102,187,106,0.08)', border: 'rgba(102,187,106,0.15)' },
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} style={{
            flex: 1, background: bg,
            border: `1.5px dashed ${border}`,
            borderRadius: 4, padding: '12px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 24, fontWeight: 500, color, lineHeight: 1, fontFamily: "'Inter', sans-serif" }}>{value}</div>
            <div style={{ fontSize: 10, color: '#666666', marginTop: 4, fontWeight: 500, letterSpacing: 0.3, fontFamily: "'Inter', sans-serif" }}>
              {label.toUpperCase()}
            </div>
          </div>
        ))}
      </div>

      {/* Milestone progress */}
      {nextMilestone && (
        <div style={{
          padding: '10px 14px', marginBottom: 12,
          border: '1.5px dashed rgba(124,77,255,0.2)',
          borderRadius: 4, background: 'rgba(237,231,246,0.3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontFamily: "'Patrick Hand', cursive", color: '#5E35B1' }}>
              {currentMilestone ? `${currentMilestone.icon} ${currentMilestone.name}` : 'First milestone'}
              {' → '}{nextMilestone.icon} {nextMilestone.name}
            </span>
            <span style={{ fontSize: 12, fontFamily: "'Inter', sans-serif", color: '#7E57C2', fontWeight: 500 }}>
              {totalDreams}/{nextMilestone.count}
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'rgba(124,77,255,0.1)', overflow: 'hidden' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((totalDreams / nextMilestone.count) * 100, 100)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              style={{ height: '100%', borderRadius: 3, background: '#7E57C2' }}
            />
          </div>
          <p style={{
            fontSize: 13, fontFamily: "'Patrick Hand', cursive",
            color: '#7E57C2', margin: '6px 0 0', textAlign: 'center',
          }}>
            {nextMilestone.count - totalDreams} more dream{nextMilestone.count - totalDreams !== 1 ? 's' : ''} to go!
          </p>
        </div>
      )}

      {/* Subject filter pills */}
      {subjects.length > 1 && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12,
        }}>
          {['All', ...subjects].map(s => {
            const isActive = filter === s;
            const pillColor = s === 'All' ? '#448AFF' : getAccentColor(s);
            return (
              <button
                key={s}
                onClick={() => setFilter(s)}
                style={{
                  fontSize: 13, fontFamily: "'Patrick Hand', cursive",
                  padding: '4px 12px', borderRadius: 14,
                  border: isActive ? `1.5px solid ${pillColor}` : '1.5px dashed #AAAAAA',
                  background: isActive ? (s === 'All' ? 'rgba(68,138,255,0.1)' : `${pillColor}15`) : 'transparent',
                  color: isActive ? pillColor : '#666666',
                  cursor: 'pointer', lineHeight: 1.2,
                  minHeight: 32,
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}

      {/* Diary entries */}
      {filtered.map((entry, i) => {
        const subject = getSubjectFromEntry(entry);
        const topic = getTopicFromEntry(entry);
        const accent = getAccentColor(subject);
        const displayHook = (entry.dream_hook || '').replace(/\biki\b/gi, petName);

        return (
          <motion.div
            key={entry.dream_id + '-' + entry.dream_date}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            style={{
              background: '#FFFFFF',
              border: '1.5px dashed #CCCCCC',
              borderLeft: `3px solid ${accent}`,
              borderRadius: 4,
              padding: 14,
              marginBottom: 8,
              position: 'relative',
            }}
          >
            {/* Date badge */}
            <span style={{
              position: 'absolute', top: 10, right: 10,
              fontSize: 11, fontFamily: "'Inter', sans-serif", fontWeight: 500,
              color: '#999999',
            }}>
              {formatDiaryDate(entry.dream_date)}
            </span>

            {/* Dream hook */}
            <p style={{
              fontSize: 15, fontFamily: "'Patrick Hand', cursive",
              color: '#1A1A2E', lineHeight: 1.4,
              margin: '0 0 10px', paddingRight: 60,
            }}>
              {displayHook || topic || 'A dream explored'}
            </p>

            {/* Bottom row: subject pill + quiz score + stars */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 12, fontFamily: "'Patrick Hand', cursive",
                padding: '2px 10px', borderRadius: 12,
                border: `1px dashed ${accent}`,
                color: accent, lineHeight: 1.2,
              }}>
                {subject}
              </span>
              {entry.quiz_score != null && (
                <span style={{
                  fontSize: 12, fontFamily: "'Inter', sans-serif", fontWeight: 500,
                  color: '#555555',
                }}>
                  Score: {entry.quiz_score}/3
                </span>
              )}
              {entry.stars_earned > 0 && (
                <span style={{ fontSize: 12 }}>
                  {'⭐'.repeat(Math.min(entry.stars_earned, 5))}
                </span>
              )}
            </div>
          </motion.div>
        );
      })}

      {filtered.length === 0 && filter !== 'All' && (
        <div style={{
          textAlign: 'center', padding: '20px 16px',
          border: '1.5px dashed #DDDDDD', borderRadius: 4,
          background: '#FFFFFF',
        }}>
          <p style={{
            fontSize: 15, fontFamily: "'Patrick Hand', cursive",
            color: '#999999', margin: 0,
          }}>
            No {filter} dreams yet — explore one on the Home tab!
          </p>
        </div>
      )}
    </div>
  );
}
